import { createServer, Response } from 'miragejs'
import React from 'react'
import { ulid } from 'ulid'
import thumbnailImage from '../Thumbnail/mocks/images/thumb_104x104_1.png'
import type { TreeDefaultNode } from '../Tree/Tree.types'

export const MOCK_UPLOAD_FILES_URL = '/mock-upload-files'
export const MOCK_UPLOAD_WITH_ERRORS_FILES_URL =
  '/mock-upload-files-with-errors'
export const MOCK_BACKEND_LATENCY = 500

const wait = (ms: number) => new Promise((res) => setTimeout(res, ms))

export const useMockBackend = () => {
  React.useEffect(() => {
    const server = createServer({
      routes() {
        this.post(MOCK_UPLOAD_FILES_URL, async (_, request) => {
          const { delay = '0' } = request.queryParams as { delay: string }
          await wait(Number(delay) * 1000)

          const remoteFileId = ulid()
          return { remoteFileId }
        })

        this.post(MOCK_UPLOAD_WITH_ERRORS_FILES_URL, async (_, request) => {
          const { delay = '0' } = request.queryParams as { delay: string }
          await wait(Number(delay) * 1000)

          return new Response(500, { error: 'Upload Error' })
        })
      },
      timing: MOCK_BACKEND_LATENCY,
    })
    return () => {
      server.shutdown()
    }
  }, [])
}

export const gridItemsSample = [
  { id: '0001', name: '0001.png', src: thumbnailImage },
  { id: '0002', name: '0002.zip' },
  { id: '0003', name: '0003.rtf', src: thumbnailImage },
  { id: '0004', name: '0004.png' },
  { id: '0005', name: '0005.docx', src: thumbnailImage },
  { id: '0006', name: '0006.unss' },
  { id: '0007', name: '0007.zip' },
  { id: '0008', name: '0008.dll' },
  { id: '0009', name: '0009.dll' },
  { id: '00010', name: '0010.png' },
  { id: '00011', name: '0011.png' },
  { id: '00012', name: '0012.png' },
]

export const groupedGridItemsSample = [
  {
    id: '0001',
    name: '0001.png',
    src: thumbnailImage,
    prostore_file_id: 1,
    sourceId: 'photos',
    groupId: '1',
  },
  {
    id: '0002',
    name: '0002.zip',
    prostore_file_id: 2,
    sourceId: 'photos',
    groupId: '1',
  },
  {
    id: '0003',
    name: '0003.rtf',
    src: thumbnailImage,
    prostore_file_id: 4,
    sourceId: 'photos',
    groupId: '1',
  },
  { id: '0006', name: '0006.unss', prostore_file_id: 6, sourceId: 'photos' },
  { id: '0007', name: '0007.zip', prostore_file_id: 7, sourceId: 'photos' },
]

export const photosCategories = [
  { id: 1, name: 'Category 1' },
  { id: 2, name: 'Unclasified' },
]

export const photosByCategory = [
  {
    id: '0001',
    name: '0001.png',
    src: thumbnailImage,
    categoryId: 1,
    label: 'test 1',
    prostore_file_id: 1,
    sourceId: 'photos',
  },
  {
    id: '0002',
    name: '0002.zip',
    categoryId: 1,
    label: 'test 2',
    prostore_file_id: 2,
    sourceId: 'photos',
  },
  {
    id: '0003',
    name: '0003.rtf',
    categoryId: 1,
    label: 'test 3',
    prostore_file_id: 3,
    sourceId: 'photos',
  },
  {
    id: '0004',
    name: '0004.png',
    categoryId: 1,
    label: 'test 4',
    prostore_file_id: 4,
    sourceId: 'photos',
  },
  {
    id: '0005',
    name: '0005.docx',
    src: thumbnailImage,
    categoryId: 1,
    label: 'test 5',
    prostore_file_id: 5,
    sourceId: 'photos',
  },
  {
    id: '0006',
    name: '0006.unss',
    categoryId: 1,
    label: 'test 6',
    prostore_file_id: 6,
    sourceId: 'photos',
  },
  {
    id: '0007',
    name: '0007.zip',
    categoryId: 2,
    label: 'test 7',
    prostore_file_id: 7,
    sourceId: 'photos',
  },
  {
    id: '0008',
    name: '0008.dll',
    categoryId: 2,
    label: 'test 8',
    prostore_file_id: 8,
    sourceId: 'photos',
  },
  {
    id: '0009',
    name: '0009.dll',
    categoryId: 2,
    label: 'test 9',
    prostore_file_id: 9,
    sourceId: 'photos',
  },
  {
    id: '00010',
    name: '0010.png',
    categoryId: 2,
    label: 'test 10',
    prostore_file_id: 10,
    sourceId: 'photos',
  },
]

export const groupItemsSample = [
  { id: '1', title: 'Group #1' },
  { id: '2', title: 'Group #2' },
]

export const fileTreeSample = [
  {
    id: 1,
    name: 'Root Folder',
    parentId: null,
    type: 'branch',
  },
  { id: 2, name: 'Animals', parentId: 1, type: 'branch' },
  { id: 3, name: 'Birds', parentId: 1, type: 'branch' },
  { id: 4, name: 'Cat.jpg', parentId: 2, type: 'leaf' },
  { id: 5, name: 'Dog.jpg', parentId: 2, type: 'leaf' },
  { id: 6, name: 'Woodpecker.jpg', parentId: 3, type: 'leaf' },
  { id: 7, name: 'Owl.jpg', parentId: 3, type: 'leaf' },
  { id: 12, name: 'Falcon.jpg', parentId: 3, type: 'leaf' },
  { id: 13, name: 'Parrot.jpg', parentId: 3, type: 'leaf' },
  { id: 14, name: 'Crane.jpg', parentId: 3, type: 'leaf' },
  { id: 15, name: 'Ostrich.jpg', parentId: 3, type: 'leaf' },
  { id: 122, name: 'Falcon.jpg', parentId: 3, type: 'leaf' },
  { id: 132, name: 'Parrot.jpg', parentId: 3, type: 'leaf' },
  { id: 142, name: 'Crane.jpg', parentId: 3, type: 'leaf' },
  { id: 152, name: 'Ostrich.jpg', parentId: 3, type: 'leaf' },
] as TreeDefaultNode[]
